import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileSpreadsheet, Upload, CheckCircle2, AlertCircle, Package, ChevronLeft, Eye } from "lucide-react";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { uploadInChunks } from "@/lib/chunkedUpload";
import { useLanguage } from "@/context/LanguageContext";
import { useProductTypes } from "@/lib/productTypes";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";

// Target fields the user can map an Excel column to.
// Must match backend UNIVERSAL_FIELDS.
const MAPPABLE_FIELDS = [
  "item_number", "description", "specification", "material", "package", "moq",
  "photo", "row_size", "row_price",
  "qty_per_ctn", "container_qty_40", "cbm_ctn",
  "case_length", "case_width", "case_height", "case_weight",
  "duty",
];

export function ImportExcelDialog({ open, onOpenChange, onImported }) {
  const { t, lang } = useLanguage();
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const productTypes = useProductTypes({ onlyEnabled: true });

  // step 1: upload
  const [file, setFile] = useState(null);
  const [uploadId, setUploadId] = useState(null);
  const [excelFormat, setExcelFormat] = useState("universal");
  const [costCurrency, setCostCurrency] = useState("USD");
  // 2026-02-26 · Preset product-type applied to every product in this import.
  // 2026-07-28 · Reverted to strict Select — must pick from Settings dictionary.
  const [productType, setProductType] = useState("");
  // 2026-07-28 · New preset field: 产品类别 (category), pulled from /settings.categories.
  const [category, setCategory] = useState("");
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    // Load categories once when the dialog first opens.
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/settings");
        if (!cancelled) setCategories(Array.isArray(data?.categories) ? data.categories : []);
      } catch { /* silent — dropdown just stays empty */ }
    })();
    return () => { cancelled = true; };
  }, [open]);

  // step 2: mapping
  const [analysis, setAnalysis] = useState(null);
  const [selectedSheet, setSelectedSheet] = useState("");
  const [headerRow, setHeaderRow] = useState(1);
  const [mapping, setMapping] = useState({});
  const [multiVariant, setMultiVariant] = useState(false);

  // step 3: preview
  const [preview, setPreview] = useState(null);
  const [checked, setChecked] = useState({});
  const [result, setResult] = useState(null);
  // 2026-02 · Overwrite-duplicates checkbox removed per user spec: duplicates
  // are now auto-suffixed (`-2`, `-3`…) on commit, so the toggle only added
  // confusion. Backend still honours ``overwrite_duplicates`` for legacy
  // callers, but the UI no longer offers it.

  const [busy, setBusy] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);

  const reset = () => {
    setFile(null); setUploadId(null); setExcelFormat("universal"); setCostCurrency("USD");
    setProductType(""); setCategory("");
    setAnalysis(null); setSelectedSheet(""); setHeaderRow(1); setMapping({}); setMultiVariant(false);
    setPreview(null); setChecked({}); setResult(null); setBusy(false); setUploadPct(0);
  };

  const step = result ? "result" : preview ? "preview" : analysis ? "map" : "upload";

  // ---------- step 1 -> next ----------
  const handleNextFromUpload = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const uid = await uploadInChunks(file, setUploadPct);
      setUploadId(uid);
      if (excelFormat === "universal") {
        const fd = new FormData();
        fd.append("upload_id", uid);
        const { data } = await api.post("/import/excel/analyze", fd, { timeout: 120000 });
        if (!data.sheets?.length) {
          toast.error(t("import.noSheets"));
          return;
        }
        const chosen = data.sheets.find((s) => s.name === data.suggested_sheet) || data.sheets[0];
        setAnalysis(data);
        setSelectedSheet(chosen.name);
        setHeaderRow(chosen.header_row);
        setMapping(chosen.suggested_mapping || {});
      } else {
        await runPreview(uid, excelFormat, null);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("import.error"));
    } finally {
      setBusy(false);
      setUploadPct(0);
    }
  };

  // ---------- step 2 -> preview ----------
  const runPreview = async (uid, format, universalArgs) => {
    const fd = new FormData();
    fd.append("upload_id", uid);
    fd.append("excel_format", format);
    if (universalArgs) {
      fd.append("sheet_name", universalArgs.sheet_name);
      fd.append("header_row", String(universalArgs.header_row));
      fd.append("column_mapping", JSON.stringify(universalArgs.column_mapping));
      fd.append("multi_variant", String(universalArgs.multi_variant));
    }
    const { data } = await api.post("/import/excel/preview", fd, { timeout: 180000 });
    setPreview(data);
    const initial = {};
    data.products.forEach((p, i) => { initial[i] = !p.exists; });
    setChecked(initial);
    if (!data.price_column_found) toast.warning(t("import.noPriceColumn"));
  };

  const handleParseFromMapping = async () => {
    if (!uploadId || !selectedSheet) return;
    // ensure item_number is mapped somewhere
    const values = Object.values(mapping);
    if (!values.includes("item_number") && !values.includes("description")) {
      toast.error(t("import.needItemOrDesc"));
      return;
    }
    setBusy(true);
    try {
      await runPreview(uploadId, "universal", {
        sheet_name: selectedSheet,
        header_row: headerRow,
        column_mapping: mapping,
        multi_variant: multiVariant,
      });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("import.error"));
    } finally {
      setBusy(false);
    }
  };

  // ---------- step 3 -> commit ----------
  const handleCommit = async () => {
    const products = preview.products.filter((_, i) => checked[i]);
    if (!products.length) return;
    setBusy(true);
    try {
      const { data } = await api.post(
        "/import/excel/commit",
        {
          cost_currency: costCurrency,
          product_type: productType || null,
          category: category || null,
          products,
        },
        { timeout: 180000 },
      );
      setResult(data);
      setPreview(null);
      toast.success(`${t("import.success")}: ${data.total_imported}`);
      onImported?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("import.error"));
    } finally {
      setBusy(false);
    }
  };

  const selectedCount = Object.values(checked).filter(Boolean).length;
  const allSelected = preview ? selectedCount === preview.products.length && preview.products.length > 0 : false;
  const toggleAll = (v) => {
    const next = {};
    preview.products.forEach((_, i) => { next[i] = Boolean(v); });
    setChecked(next);
  };

  const currentSheet = analysis?.sheets.find((s) => s.name === selectedSheet);
  const dialogWidth = step === "preview" ? "max-w-6xl" : step === "map" ? "max-w-3xl" : "max-w-lg";

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent data-testid="import-excel-dialog" className={`${dialogWidth} max-h-[85vh] overflow-y-auto`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet size={18} className="text-green-700" /> {t("import.title")}
            <StepBadge step={step} />
          </DialogTitle>
          <DialogDescription className="text-xs">
            {step === "upload" && t("import.hint")}
            {step === "map" && t("import.mapHint")}
            {step === "preview" && t("import.previewHint")}
          </DialogDescription>
        </DialogHeader>

        {/* ============ STEP 1: UPLOAD ============ */}
        {step === "upload" && (
          <>
            <input
              ref={inputRef}
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              data-testid="import-excel-file-input"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              data-testid="import-excel-dropzone"
              className="w-full flex flex-col items-center gap-2 border-2 border-dashed border-border rounded-md py-8 hover:border-brand hover:bg-brand/5 transition-colors"
            >
              <Upload size={24} className="text-muted-foreground" />
              <span className="text-sm font-medium">{file ? file.name : t("import.dropzone")}</span>
            </button>
            <div className="space-y-2" data-testid="import-format">
              <p className="font-mono text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{t("import.format")}</p>
              {[
                ["universal", t("import.formatUniversal"), t("import.formatUniversalHint")],
                ["wise", t("import.formatWise"), null],
                ["betterhome", t("import.formatBetterHome"), null],
              ].map(([value, label, hint]) => (
                <label key={value} className={`flex items-start gap-2.5 border rounded-md px-3 py-2 cursor-pointer text-sm transition-colors ${excelFormat === value ? "border-brand bg-brand/5" : "border-border hover:border-brand/40"}`}>
                  <input
                    type="radio"
                    name="excel-format"
                    value={value}
                    checked={excelFormat === value}
                    onChange={() => {
                      setExcelFormat(value);
                      if (value === "betterhome") setCostCurrency("USD");
                    }}
                    data-testid={`import-format-${value}`}
                    className="accent-[#2563EB] mt-0.5"
                  />
                  <span className="flex-1">
                    <span className="block">{label}</span>
                    {hint && <span className="block text-[11px] text-muted-foreground">{hint}</span>}
                  </span>
                </label>
              ))}
            </div>
            <div className="space-y-2" data-testid="import-cost-currency">
              <p className="font-mono text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{t("import.costCurrency")}</p>
              {[
                ["RMB", t("import.costRmb")],
                ["USD", t("import.costUsd")],
              ].map(([value, label]) => (
                <label key={value} className={`flex items-center gap-2.5 border rounded-md px-3 py-2 cursor-pointer text-sm transition-colors ${costCurrency === value ? "border-brand bg-brand/5" : "border-border hover:border-brand/40"}`}>
                  <input
                    type="radio"
                    name="cost-currency"
                    value={value}
                    checked={costCurrency === value}
                    onChange={() => setCostCurrency(value)}
                    data-testid={`import-cost-currency-${value.toLowerCase()}`}
                    className="accent-[#2563EB]"
                  />
                  {label}
                </label>
              ))}
            </div>
            {/* 2026-02-26 · Preset product type for every imported row.
                2026-07-28 · Reverted to a strict dropdown (no custom text input);
                admin manages the list under Settings → Product Types. */}
            <div className="space-y-2" data-testid="import-product-type-block">
              <p className="font-mono text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                {t("import.productType")}
              </p>
              <Select
                value={productType || "__none__"}
                onValueChange={(v) => setProductType(v === "__none__" ? "" : v)}
              >
                <SelectTrigger data-testid="import-product-type-select" className="h-9">
                  <SelectValue placeholder={t("import.productTypeNone")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__" data-testid="import-product-type-option-none">
                    <span className="text-muted-foreground">— {t("import.productTypeNone")} —</span>
                  </SelectItem>
                  {productTypes.map((tp) => (
                    <SelectItem
                      key={tp.code}
                      value={tp.code}
                      data-testid={`import-product-type-option-${tp.code}`}
                    >
                      {(lang === "en" ? tp.name_en : tp.name_zh) || tp.code}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground">{t("import.productTypeHint")}</p>
            </div>

            {/* 2026-07-28 · Preset product category — options pulled from
                Settings → Categories. Applied to every imported row. */}
            <div className="space-y-2" data-testid="import-category-block">
              <p className="font-mono text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                {t("import.category")}
              </p>
              <Select
                value={category || "__none__"}
                onValueChange={(v) => setCategory(v === "__none__" ? "" : v)}
              >
                <SelectTrigger data-testid="import-category-select" className="h-9">
                  <SelectValue placeholder={t("import.categoryNone")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__" data-testid="import-category-option-none">
                    <span className="text-muted-foreground">— {t("import.categoryNone")} —</span>
                  </SelectItem>
                  {categories.map((c) => (
                    <SelectItem key={c} value={c} data-testid={`import-category-option-${c}`}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground">{t("import.categoryHint")}</p>
            </div>
            <Button
              onClick={handleNextFromUpload}
              disabled={!file || busy}
              data-testid="import-excel-submit-button"
              className="rounded-md bg-brand hover:bg-brand-hover transition-colors"
            >
              {busy
                ? (uploadPct > 0 && uploadPct < 100 ? `${t("import.uploading")} ${uploadPct}%` : t("import.parsing"))
                : (excelFormat === "universal" ? t("import.next") : t("import.preview"))}
            </Button>
          </>
        )}

        {/* ============ STEP 2: MAPPING ============ */}
        {step === "map" && currentSheet && (
          <div className="space-y-3" data-testid="import-mapping">
            <div className="grid grid-cols-3 gap-3 items-end">
              <div className="col-span-2">
                <label className="font-mono text-[10px] font-medium uppercase tracking-wider text-muted-foreground block mb-1">
                  {t("import.sheet")}
                </label>
                <Select
                  value={selectedSheet}
                  onValueChange={(v) => {
                    setSelectedSheet(v);
                    const s = analysis.sheets.find((x) => x.name === v);
                    if (s) { setHeaderRow(s.header_row); setMapping(s.suggested_mapping || {}); }
                  }}
                >
                  <SelectTrigger data-testid="import-sheet-select" className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {analysis.sheets.map((s) => (
                      <SelectItem key={s.name} value={s.name} data-testid={`import-sheet-option-${s.name}`}>
                        {s.name} <span className="text-muted-foreground text-xs">({s.row_count} rows)</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="font-mono text-[10px] font-medium uppercase tracking-wider text-muted-foreground block mb-1">
                  {t("import.headerRow")}
                </label>
                <Input
                  type="number"
                  min={1}
                  value={headerRow}
                  onChange={(e) => setHeaderRow(Math.max(1, parseInt(e.target.value) || 1))}
                  data-testid="import-header-row-input"
                  className="h-9"
                />
              </div>
            </div>

            <div className="border border-border rounded-md overflow-auto max-h-[45vh]">
              <table className="w-full text-sm" data-testid="import-mapping-table">
                <thead className="sticky top-0 bg-secondary/95 z-10">
                  <tr className="border-b border-border">
                    <th className="text-left text-[10px] font-mono uppercase tracking-wider text-muted-foreground px-2 py-1.5 w-10">#</th>
                    <th className="text-left text-[10px] font-mono uppercase tracking-wider text-muted-foreground px-2 py-1.5">{t("import.excelColumn")}</th>
                    <th className="text-left text-[10px] font-mono uppercase tracking-wider text-muted-foreground px-2 py-1.5">{t("import.sampleValues")}</th>
                    <th className="text-left text-[10px] font-mono uppercase tracking-wider text-muted-foreground px-2 py-1.5 w-52">{t("import.mapTo")}</th>
                  </tr>
                </thead>
                <tbody>
                  {currentSheet.columns.map((col) => {
                    const currentField = mapping[String(col.col)] || "ignore";
                    return (
                      <tr key={col.col} className="border-b border-border/60" data-testid={`import-mapping-row-${col.col}`}>
                        <td className="px-2 py-1.5 font-mono text-xs text-muted-foreground">{col.col}</td>
                        <td className="px-2 py-1.5 font-medium">{col.header}</td>
                        <td className="px-2 py-1.5 text-xs text-muted-foreground">
                          {col.samples.length ? col.samples.map((s, si) => (
                            <span key={si} className="inline-block max-w-[220px] truncate bg-secondary/60 rounded px-1.5 mr-1" title={s}>{s}</span>
                          )) : <span className="italic">—</span>}
                        </td>
                        <td className="px-2 py-1.5">
                          <Select
                            value={currentField}
                            onValueChange={(v) => {
                              setMapping((m) => {
                                const next = { ...m };
                                if (v === "ignore") delete next[String(col.col)];
                                else {
                                  // uniqueness: if same field was mapped elsewhere, clear it
                                  for (const k of Object.keys(next)) if (next[k] === v) delete next[k];
                                  next[String(col.col)] = v;
                                }
                                return next;
                              });
                            }}
                          >
                            <SelectTrigger data-testid={`import-mapping-select-${col.col}`} className="h-8 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="ignore" data-testid={`import-mapping-option-ignore-${col.col}`}>
                                <span className="text-muted-foreground">— {t("import.ignore")} —</span>
                              </SelectItem>
                              {MAPPABLE_FIELDS.map((f) => (
                                <SelectItem key={f} value={f} data-testid={`import-mapping-option-${f}-${col.col}`}>
                                  {t(`import.field.${f}`)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="import-multi-variant-label">
              <Checkbox
                checked={multiVariant}
                onCheckedChange={(v) => setMultiVariant(Boolean(v))}
                data-testid="import-multi-variant-check"
              />
              <span>{t("import.multiVariant")}</span>
              <span className="text-[11px] text-muted-foreground">— {t("import.multiVariantHint")}</span>
            </label>

            <div className="flex gap-2 justify-end pt-1">
              <Button
                variant="outline"
                onClick={() => { setAnalysis(null); setUploadId(null); }}
                data-testid="import-mapping-back-button"
                className="rounded-md"
                disabled={busy}
              >
                <ChevronLeft size={14} /> {t("common.back")}
              </Button>
              <Button
                onClick={handleParseFromMapping}
                disabled={busy}
                data-testid="import-mapping-preview-button"
                className="rounded-md bg-brand hover:bg-brand-hover transition-colors"
              >
                {busy ? t("import.parsing") : t("import.preview")}
              </Button>
            </div>
          </div>
        )}

        {/* ============ STEP 3: PREVIEW ============ */}
        {step === "preview" && preview && (
          <div className="space-y-3" data-testid="import-preview">
            <div className="flex items-center flex-wrap gap-3 justify-between">
              <p className="text-sm font-semibold">
                {t("import.parsed")}: {preview.total} · {t("import.selectedCount")}: {selectedCount}
              </p>
            </div>
            <div className="border border-border rounded-md overflow-auto max-h-[52vh]">
              <table className="w-full text-sm" data-testid="import-preview-table">
                <thead className="sticky top-0 bg-secondary/90 z-10">
                  <tr className="border-b border-border">
                    <th className="w-9 px-2 py-1.5">
                      <Checkbox
                        checked={allSelected ? true : selectedCount > 0 ? "indeterminate" : false}
                        onCheckedChange={(v) => toggleAll(v === true)}
                        data-testid="import-preview-check-all"
                        title={allSelected ? t("import.deselectAll") : t("import.selectAll")}
                      />
                    </th>
                    {[
                      [t("quote.photo"), "w-14"],
                      [t("products.itemNumber"), ""],
                      [t("quote.description"), ""],
                      [t("products.material"), ""],
                      [t("products.package"), ""],
                      [t("products.moq"), ""],
                      [t("import.priceDetails"), ""],
                    ].map(([label, cls]) => (
                      <th key={label} className={`text-left text-[10px] font-mono uppercase tracking-wider text-muted-foreground px-2 py-1.5 whitespace-nowrap ${cls}`}>{label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.products.map((p, i) => (
                    <tr key={i} data-testid={`import-preview-row-${i}`} className={`border-b border-border/60 ${p.exists ? "opacity-60 bg-amber-50" : ""}`}>
                      <td className="px-2 py-1.5 align-top">
                        <Checkbox
                          checked={Boolean(checked[i])}
                          onCheckedChange={(v) => setChecked((c) => ({ ...c, [i]: Boolean(v) }))}
                          data-testid={`import-preview-check-${i}`}
                        />
                      </td>
                      <td className="px-2 py-1.5 align-top">
                        {p.photo_url ? (
                          <img src={`${API.replace("/api", "")}${p.photo_url}`} alt="" className="w-14 h-12 rounded-sm object-contain bg-white border border-border" />
                        ) : (
                          <div className="w-12 h-12 rounded-sm bg-secondary flex items-center justify-center">
                            <Package size={14} className="text-muted-foreground" />
                          </div>
                        )}
                      </td>
                      <td className="px-2 py-1.5 align-top whitespace-nowrap">
                        <span className="font-mono font-semibold text-xs">{p.item_number}</span>
                        {p.exists && <span className="block text-[10px] text-amber-700">{t("import.exists")}</span>}
                      </td>
                      <td className="px-2 py-1.5 align-top text-xs max-w-[220px]">
                        <p className="line-clamp-2" title={p.description}>{p.description || "-"}</p>
                        {p.specification && <p className="line-clamp-2 text-muted-foreground" title={p.specification}>{p.specification}</p>}
                      </td>
                      <td className="px-2 py-1.5 align-top text-xs max-w-[140px]">
                        <p className="line-clamp-3" title={p.material}>{p.material || "-"}</p>
                      </td>
                      <td className="px-2 py-1.5 align-top text-xs max-w-[120px]">
                        <p className="line-clamp-3" title={p.package}>{p.package || "-"}</p>
                      </td>
                      <td className="px-2 py-1.5 align-top text-xs max-w-[110px]">
                        <p className="line-clamp-3" title={p.moq}>{p.moq || "-"}</p>
                      </td>
                      <td className="px-2 py-1.5 align-top font-mono text-[11px] whitespace-nowrap">
                        {p.price_rows.length === 0 && "-"}
                        {p.price_rows.map((r, ri) => (
                          <div key={ri} className="flex gap-2">
                            <span className="max-w-[180px] truncate" title={r.size}>{r.size || "-"}</span>
                            <span className="font-semibold">{r.price || "-"}</span>
                            {Boolean(r.qty_per_ctn) && <span className="text-muted-foreground">{r.qty_per_ctn}/CTN</span>}
                            {Boolean(r.cbm_ctn) && <span className="text-muted-foreground">{r.cbm_ctn}m³</span>}
                            {Boolean(r.container_qty_40) && <span className="text-muted-foreground">{`40'HQ:${r.container_qty_40}`}</span>}
                          </div>
                        ))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => {
                  setPreview(null);
                  setChecked({});
                  // if universal, go back to map step; otherwise reset upload
                  if (!analysis) reset();
                }}
                data-testid="import-preview-back-button"
                className="rounded-md"
              >
                <ChevronLeft size={14} /> {t("common.back")}
              </Button>
              <Button
                onClick={handleCommit}
                disabled={busy || selectedCount === 0}
                data-testid="import-confirm-button"
                className="rounded-md bg-brand hover:bg-brand-hover transition-colors"
              >
                {busy ? t("import.importing") : `${t("import.confirm")} (${selectedCount})`}
              </Button>
            </div>
          </div>
        )}

        {/* ============ STEP 4: RESULT ============ */}
        {step === "result" && result && (
          <div className="space-y-3" data-testid="import-excel-result">
            <p className="text-sm font-semibold flex items-center gap-1.5">
              <CheckCircle2 size={16} className="text-green-600" />
              {t("import.imported")}: {result.total_imported} / {t("import.skipped")}: {result.total_skipped}
              {result.total_replaced ? (
                <span className="text-amber-700"> / {t("import.replaced")}: {result.total_replaced}</span>
              ) : null}
            </p>
            <div className="space-y-1.5 max-h-64 overflow-y-auto">
              {result.imported.map((p) => (
                <div key={p.item_number} className={`text-sm border rounded-lg px-3 py-2 flex items-center justify-between gap-2 ${p.replaced ? "border-amber-300 bg-amber-50" : p.renamed_from ? "border-sky-300 bg-sky-50" : "border-border"}`}>
                  <span className="truncate">
                    <b>{p.item_number}</b> — {p.description}
                    {p.replaced && (
                      <span className="ml-2 inline-block px-1.5 py-0.5 rounded-sm bg-amber-200 text-amber-900 text-[10px] font-semibold uppercase">
                        {t("import.replaced")}
                      </span>
                    )}
                    {p.renamed_from && (
                      <span className="ml-2 inline-block px-1.5 py-0.5 rounded-sm bg-sky-200 text-sky-900 text-[10px] font-semibold" data-testid={`import-renamed-${p.item_number}`}>
                        {t("import.renamedFrom")} {p.renamed_from}
                      </span>
                    )}
                  </span>
                  <span className="text-xs text-muted-foreground shrink-0">
                    {p.price_rows} {t("import.rows")}{p.has_photo ? " · 📷" : ""}
                  </span>
                </div>
              ))}
              {result.skipped.map((p) => (
                <div key={p.item_number} className="text-sm border border-amber-200 bg-amber-50 rounded-lg px-3 py-2 flex items-center gap-2">
                  <AlertCircle size={14} className="text-amber-600 shrink-0" />
                  <span className="truncate">{p.item_number} — {p.reason}</span>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {result.total_imported > 0 && (
                <Button
                  onClick={() => {
                    const items = (result.imported || []).map((p) => p.item_number).filter(Boolean).join(",");
                    onOpenChange(false);
                    reset();
                    navigate(`/?items=${encodeURIComponent(items)}`);
                  }}
                  data-testid="import-view-imported-button"
                  className="rounded-md flex-1 bg-brand hover:bg-brand-hover text-white"
                >
                  <Eye size={14} /> {t("import.viewImported")}
                </Button>
              )}
              <Button variant="outline" onClick={() => { onOpenChange(false); reset(); }} data-testid="import-excel-close-button" className="rounded-md flex-1">
                {t("common.close")}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Tiny 1-2-3-4 step indicator next to the dialog title.
const StepBadge = ({ step }) => {
  const order = ["upload", "map", "preview", "result"];
  const idx = order.indexOf(step);
  return (
    <span className="ml-auto text-[10px] font-mono uppercase tracking-wider text-muted-foreground" data-testid="import-step-badge">
      {order.map((s, i) => (
        <span key={s} className={`inline-block w-5 text-center rounded-sm ${i === idx ? "bg-brand text-white" : i < idx ? "text-brand" : "opacity-30"}`}>{i + 1}</span>
      ))}
    </span>
  );
};
